/**
 * LayoutDataContext - Centralized data fetching for Layout components
 *
 * This context fetches all layout-related data in parallel to avoid sequential requests:
 * - Staff locations (for LocationSelector)
 * - Clock status (for ClockInOutButton)
 * - Room configuration (for Layout room indicator)
 *
 * Performance: Reduces 3 sequential requests (~600-900ms) to 1 parallel fetch (~200-300ms)
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { locationService } from '@/domains/internal/facilities/services/locationService';
import { timeTrackingService } from '@/domains/internal/staff/services/timeTrackingService';
import { getRoomConfigAsync, RoomConfig } from '@/config/roomConfig';
import { useAuth } from './AuthContext';
import { logger } from '@/utils/logger';

// Import types from API definitions
import type { StaffLocationsResponse } from '@/shared/types/api';

// Clock status type (matches what timeTrackingService.getClockStatus() returns)
interface ClockStatus {
  is_clocked_in: boolean;
  clock_in_time: string | null;
  duration_hours: number | null;
  time_log_id: string | null;
}

interface LayoutData {
  locations: StaffLocationsResponse | null;
  clockStatus: ClockStatus | null;
  roomConfig: RoomConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const LayoutDataContext = createContext<LayoutData | undefined>(undefined);

export const LayoutDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthReady, isAuthenticated } = useAuth();
  const [locations, setLocations] = useState<StaffLocationsResponse | null>(null);
  const [clockStatus, setClockStatus] = useState<ClockStatus | null>(null);
  const [roomConfig, setRoomConfig] = useState<RoomConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllData = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated) {
      logger.debug('[LayoutDataContext] Waiting for auth to be ready...');
      setLoading(false);
      return;
    }

    logger.debug('[LayoutDataContext] Fetching all layout data in parallel...');
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel using Promise.allSettled (doesn't fail if one fails)
      const startTime = performance.now();
      const [locationsResult, clockStatusResult, roomConfigResult] = await Promise.allSettled([
        locationService.getStaffLocations(),
        timeTrackingService.getClockStatus(),
        getRoomConfigAsync(),
      ]);

      const endTime = performance.now();
      logger.debug(`[LayoutDataContext] Parallel fetch completed in ${(endTime - startTime).toFixed(2)}ms`);

      // Process locations result
      if (locationsResult.status === 'fulfilled') {
        setLocations(locationsResult.value);
        logger.debug('[LayoutDataContext] Locations loaded successfully');
      } else {
        logger.error('[LayoutDataContext] Failed to fetch locations:', locationsResult.reason);
        setLocations(null);
      }

      // Process clock status result
      if (clockStatusResult.status === 'fulfilled') {
        setClockStatus(clockStatusResult.value);
        logger.debug('[LayoutDataContext] Clock status loaded successfully');
      } else {
        // 404 is expected on admin portal (public schema), don't log error
        if (clockStatusResult.reason?.response?.status !== 404) {
          logger.error('[LayoutDataContext] Failed to fetch clock status:', clockStatusResult.reason);
        }
        setClockStatus(null);
      }

      // Process room config result
      if (roomConfigResult.status === 'fulfilled') {
        setRoomConfig(roomConfigResult.value);
        logger.debug('[LayoutDataContext] Room config loaded successfully');
      } else {
        logger.error('[LayoutDataContext] Failed to fetch room config:', roomConfigResult.reason);
        setRoomConfig(null);
      }
    } catch (err: any) {
      logger.error('[LayoutDataContext] Unexpected error during parallel fetch:', err);
      setError(err.message || 'Failed to load layout data');
    } finally {
      setLoading(false);
    }
  }, [isAuthReady, isAuthenticated]);

  // Fetch data on mount and when auth state changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const value: LayoutData = {
    locations,
    clockStatus,
    roomConfig,
    loading,
    error,
    refetch: fetchAllData,
  };

  return <LayoutDataContext.Provider value={value}>{children}</LayoutDataContext.Provider>;
};

export const useLayoutData = () => {
  const context = useContext(LayoutDataContext);
  if (context === undefined) {
    throw new Error('useLayoutData must be used within a LayoutDataProvider');
  }
  return context;
};
